import hasher from 'hash-sum';
import { inject, injectable } from 'inversify';
import { serializeError } from 'serialize-error';
import WebRTCIssueDetector, { EventType, NetworkScores, WebRTCIssueEmitter } from 'webrtc-issue-detector';
import { CLIENT_EVENTS } from '../../../constants/events';
import EnhancedEventEmitter from '../../../EnhancedEventEmitter';
import { debounce, getSDKInstancesMarks } from '../../../helpers/common';
import { TOKEN } from '../../../inversify.tokens';
import { ChannelStateShort } from '../../../proto/generated/channel/channel_state_short';
import { LocalChannelState } from '../../../types/channelStateSyncronizer';
import { CallState } from '../../CallState';
import { RestoreChannelStateResult } from '../../ChannelStateSynchronizer/types';
import Logger from '../../Logger';
import Network from '../../network';
import PeersStorage from '../../PeersStorage';
import SignalingApiClient from '../../signalingApiClient/SignalingApiClient';
import ChannelStateConsistencyChecker from './ChannelStateConsistencyChecker';
import ChannelStateConsistencyCheckResult from './ChannelStateConsistencyCheckResult';
import {
  BAD_NETWORK_SCORE,
  EXCELLENT_NETWORK_SCORE,
  MAX_CHECK_RETRIES_BEFORE_CONFIRM,
} from './consts';
import { InconsistencyType, StorageKey } from './types';

const RESTORE_STATE_RECOMMENDED_INTERVAL = 15_000;

const HANDLE_CHANNEL_STATE_EVENT_DEBOUNCE_TIME_MS = 3_000;

@injectable()
class ChannelStateSyncEventHandler {
  readonly #logger: Logger;

  readonly #webRTCIssueEmitter?: WebRTCIssueEmitter;

  #checkResults: ChannelStateConsistencyCheckResult[] = [];

  #channelStateEventHandling = false;

  readonly #networkScores = {
    inbound: EXCELLENT_NETWORK_SCORE,
    outbound: EXCELLENT_NETWORK_SCORE,
  };

  readonly #lastSyncTime: number = new Date().getTime() - RESTORE_STATE_RECOMMENDED_INTERVAL;

  readonly debouncedHandleChannelStateEvent: (remoteState: ChannelStateShort) => Promise<void>;

  constructor(
    @inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger,
    @inject(TOKEN.Network) private readonly network: Network,
    @inject(TOKEN.CallState) private readonly callState: CallState,
    @inject(TOKEN.PeersStorage) private readonly peersStorage: PeersStorage,
    @inject(TOKEN.ClientEventEmitter) private readonly clientEventEmitter: EnhancedEventEmitter,
    @inject(TOKEN.SignalingApiClient) private readonly signalingApiClient: SignalingApiClient,
    @inject(TOKEN.WebRTCIssueDetector) private readonly webRtcIssueDetector?: WebRTCIssueDetector,
  ) {
    this.#webRTCIssueEmitter = this.webRtcIssueDetector?.eventEmitter;
    this.#logger = loggerFactory('ChannelStateSyncEventHandler');

    this.debouncedHandleChannelStateEvent = debounce(
      this.handleChannelStateEvent.bind(this),
      HANDLE_CHANNEL_STATE_EVENT_DEBOUNCE_TIME_MS,
    );

    this.listenNetworkScoresUpdatedEvents();
  }

  async handleChannelStateShort(data: ChannelStateShort): Promise<void> {
    try {
      await this.debouncedHandleChannelStateEvent(data);
    } catch (error: unknown) {
      this.#logger.error('handleChannelStateSyncEvent()', {
        error: serializeError(error),
      });
    }
  }

  private async handleChannelStateEvent(remoteState: ChannelStateShort): Promise<void> {
    if (this.#channelStateEventHandling) {
      this.#logger.warn('Channel state event is already being handled, skipping new event', {
        case: 'handleChannelStateEvent',
      });

      return;
    }

    this.#channelStateEventHandling = true;

    try {
      await this.collectProbableInconsistencyCases(remoteState);
      const confirmedInconsistencyResult = this.getConfirmedInconsistencyResult();

      if (!confirmedInconsistencyResult?.hasInconsistencyCases()) {
        return;
      }

      if (this.isRestoreStateRequestToEarly()) {
        this.#logger.info('Inconsistency detected, but restore skipped', { ...confirmedInconsistencyResult });

        return;
      }

      // const restoreResult = await this.channelStateSynchronizer.restoreState();
      // const updatedCheckResult = this.compareCheckedAndRestoredResults(confirmedInconsistencyResult, restoreResult);

      // if (!updatedCheckResult.hasInconsistencyCases()) {
      //   this.#checkResults[this.#checkResults.length - 1] = updatedCheckResult;
      //   this.#logger.info('Inconsistency has been corrected', { ...restoreResult });
      //   return;
      // }

      // this.#logger.warn('Inconsistency was detected', { ...updatedCheckResult });
      this.handleConfirmedInconsistencyResult(confirmedInconsistencyResult);
    } finally {
      this.#channelStateEventHandling = false;
    }
  }

  private getLastInconsistencyCheckResult(): ChannelStateConsistencyCheckResult {
    return this.#checkResults[this.#checkResults.length - 1];
  }

  private handleConfirmedInconsistencyResult(confirmedInconsistencyResult: ChannelStateConsistencyCheckResult): void {
    confirmedInconsistencyResult.missingPeers.forEach((peerId) => {
      this.clientEventEmitter.safeEmit(CLIENT_EVENTS.channelStateInconsistent, {
        peerId,
        peersLoaded: this.network.peersLoaded,
        sdkInstances: getSDKInstancesMarks().length,
        type: InconsistencyType.MissingPeer,
      });
    });

    confirmedInconsistencyResult.missingProducers.forEach((producerId) => {
      this.clientEventEmitter.safeEmit(CLIENT_EVENTS.channelStateInconsistent, {
        producerId,
        type: InconsistencyType.MissingProducer,
      });
    });

    confirmedInconsistencyResult.producersWithInconsistentState.forEach((producerId) => {
      this.clientEventEmitter.safeEmit(CLIENT_EVENTS.channelStateInconsistent, {
        producerId,
        type: InconsistencyType.IncorrectProducerState,
      });
    });

    confirmedInconsistencyResult.peersWithInconsistentAppData.forEach((peerId) => {
      this.clientEventEmitter.safeEmit(CLIENT_EVENTS.channelStateInconsistent, {
        peerId,
        type: InconsistencyType.IncorrectAppData,
      });
    });

    // confirmedInconsistencyResult.consumersWithInconsistentState.forEach((consumerId) => {
    //   this.clientEventEmitter.safeEmit(CLIENT_EVENTS.channelStateInconsistent, {
    //     consumerId,
    //     type: InconsistenceType.IncorrectConsumerState,
    //   });
    // });
  }

  private getConfirmedInconsistencyResult(): ChannelStateConsistencyCheckResult | undefined {
    const lastCheckResult = this.getLastInconsistencyCheckResult();

    if (!lastCheckResult) {
      return undefined;
    }

    const confirmedCheckResult = new ChannelStateConsistencyCheckResult({
      missingPeers: this.getConfirmedMissingPeers(lastCheckResult.missingPeers),
      missingProducers: this.getConfirmedMissingProducers(lastCheckResult.missingProducers),
      consumersWithInconsistentState: this.getConfirmedConsumersWithInconsistencState(
        lastCheckResult.consumersWithInconsistentState,
      ),
      producersWithInconsistentState: this.getConfirmedProducersWithInconsistencState(
        lastCheckResult.producersWithInconsistentState,
      ),
      peersWithInconsistentAppData: this.getConfirmedPeersWithInconsistentAppData(lastCheckResult.missingPeers),
    });

    return confirmedCheckResult;
  }

  private async collectProbableInconsistencyCases(remoteState: ChannelStateShort): Promise<void> {
    const checker = new ChannelStateConsistencyChecker({
      remoteState,
      localState: this.getLocalState(),
    });

    const checkResult = checker.check();
    this.rotateLastProbableInconsistencyCheckResults(checkResult);

    if (!checkResult.hasInconsistencyCases()) {
      return;
    }

    if (this.isBadNetworkScore()) {
      this.resetLastProbableInconsistencyCheckResults();

      return;
    }

    const isConnectionActive = (await this.getIsConnectionActive())
      && this.callState.isJoined && this.network.isNetworkActive;

    if (!isConnectionActive) {
      this.resetLastProbableInconsistencyCheckResults();
    }
  }

  private resetLastProbableInconsistencyCheckResults(): void {
    this.#checkResults = [];
  }

  private rotateLastProbableInconsistencyCheckResults(checkResult: ChannelStateConsistencyCheckResult): void {
    if (this.#checkResults.length === MAX_CHECK_RETRIES_BEFORE_CONFIRM) {
      this.#checkResults.shift();
    }

    this.#checkResults.push(checkResult);
  }

  private isReachedMaxCheckRetries(
    storageKey: StorageKey,
    id: string,
    maxRetries = MAX_CHECK_RETRIES_BEFORE_CONFIRM,
  ): boolean {
    let retries = 0;
    this.#checkResults.forEach((checkResults) => {
      if (checkResults[storageKey].includes(id)) {
        retries += 1;
      }
    });

    return retries >= maxRetries;
  }

  private getConfirmedMissingPeers(peerIds: string[]): string[] {
    return peerIds.filter((peerId) => this.isReachedMaxCheckRetries('missingPeers', peerId));
  }

  private getConfirmedMissingProducers(producerIds: string[]): string[] {
    return producerIds.filter((producerId) => this.isReachedMaxCheckRetries('missingProducers', producerId));
  }

  private getConfirmedConsumersWithInconsistencState(consumersIds: string[]): string[] {
    return consumersIds.filter((consumerId) => this.isReachedMaxCheckRetries(
      'consumersWithInconsistentState',
      consumerId,
      1,
    ));
  }

  private getConfirmedProducersWithInconsistencState(producerIds: string[]): string[] {
    return producerIds.filter((producerId) => this.isReachedMaxCheckRetries(
      'producersWithInconsistentState',
      producerId,
    ));
  }

  private getConfirmedPeersWithInconsistentAppData(peerIds: string[]): string[] {
    return peerIds.filter((peerId) => this.isReachedMaxCheckRetries('peersWithInconsistentAppData', peerId));
  }

  private getLocalState(): LocalChannelState {
    const hostPeers = this.peersStorage.hosts();

    return {
      peers: hostPeers.map((peer) => ({
        id: peer.id,
        appDataHash: hasher(peer.appData),
        producers: peer.getProducers().map((producer) => ({
          id: producer.id,
          paused: producer.paused,
          stateChangedAt: producer.getStateChangedAt(),
        })),
        consumers: peer.getConsumersState(),
      })),
    };
  }

  private async getIsConnectionActive(): Promise<boolean> {
    try {
      if (!this.network.isNetworkActive) {
        return false;
      }

      await this.signalingApiClient.ping.pingPeer();

      return true;
    } catch {
      return false;
    }
  }

  private listenNetworkScoresUpdatedEvents(): void {
    if (!this.#webRTCIssueEmitter) {
      return;
    }

    this.#webRTCIssueEmitter.on(EventType.NetworkScoresUpdated, (scores: NetworkScores) => {
      if (scores.inbound) {
        this.#networkScores.inbound = scores.inbound;
      }

      if (scores.outbound) {
        this.#networkScores.outbound = scores.outbound;
      }
    });
  }

  private isBadNetworkScore(): boolean {
    const { inbound, outbound } = this.#networkScores;

    return inbound <= BAD_NETWORK_SCORE || outbound <= BAD_NETWORK_SCORE;
  }

  private compareCheckedAndRestoredResults(
    checkResult: ChannelStateConsistencyCheckResult,
    restoreResult: RestoreChannelStateResult,
  ): ChannelStateConsistencyCheckResult {
    return new ChannelStateConsistencyCheckResult({
      missingPeers: checkResult.missingPeers
        .filter((id) => !restoreResult.missingPeers.includes(id)),
      missingProducers: checkResult.missingProducers
        .filter((id) => !restoreResult.missingProducers.includes(id)),
      consumersWithInconsistentState: checkResult.consumersWithInconsistentState
        .filter((id) => !restoreResult.fixedConsumersState.includes(id)),
      producersWithInconsistentState: checkResult.producersWithInconsistentState
        .filter((id) => !restoreResult.fixedProducersState.includes(id)),
      peersWithInconsistentAppData: checkResult.peersWithInconsistentAppData
        .filter((id) => !restoreResult.restoredAppDataPeers.includes(id)),
    });
  }

  private isRestoreStateRequestToEarly(): boolean {
    return (new Date().getTime() - this.#lastSyncTime) <= RESTORE_STATE_RECOMMENDED_INTERVAL;
  }
}

export default ChannelStateSyncEventHandler;
